import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '@/store/authStore';
import { CloudOff, RefreshCw, CheckCircle, AlertTriangle, Wifi, WifiOff } from 'lucide-react';
import { Button } from '../ui/Button';
import { cloudSyncService, CloudSyncState } from '@/services/cloudSyncService';

interface SyncStatusProps {
  showDetails?: boolean;
  className?: string;
}

export function SyncStatus({ showDetails = false, className = '' }: SyncStatusProps) {
  const { user, isAuthenticated } = useAuthStore();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [driveState, setDriveState] = useState<CloudSyncState>(cloudSyncService.getState());

  const isSyncing = driveState.status === 'syncing';
  const syncError = driveState.error;
  const lastSyncTime = driveState.lastSyncTime ? new Date(driveState.lastSyncTime) : null;

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Subscribe to cloud sync state
  useEffect(() => {
    return cloudSyncService.subscribe(setDriveState);
  }, []);

  const handleManualSync = useCallback(async () => {
    await cloudSyncService.performSync();
  }, []);

  const getSyncStatus = () => {
    // Check Google Drive connection first (works without account)
    if (!driveState.driveConnected && !isAuthenticated) {
      return {
        icon: CloudOff,
        label: 'Local Only',
        color: 'text-gray-400',
        description: 'Connect Google Drive to enable sync'
      };
    }

    if (!driveState.driveConnected && isAuthenticated && !user?.preferences?.enableCloudSync) {
      return {
        icon: CloudOff,
        label: 'Sync Disabled',
        color: 'text-gray-400',
        description: 'Connect Google Drive or enable cloud sync'
      };
    }

    if (!isOnline) {
      return {
        icon: WifiOff,
        label: 'Offline',
        color: 'text-yellow-500',
        description: 'Will sync when connection is restored'
      };
    }

    if (isSyncing) {
      return {
        icon: RefreshCw,
        label: 'Syncing...',
        color: 'text-blue-500',
        description: 'Uploading changes to cloud'
      };
    }

    if (syncError) {
      return {
        icon: AlertTriangle,
        label: 'Sync Error',
        color: 'text-red-500',
        description: syncError
      };
    }

    return {
      icon: CheckCircle,
      label: 'Synced',
      color: 'text-green-500',
      description: lastSyncTime
        ? `Last synced ${formatLastSync(lastSyncTime)}`
        : 'All changes synced to cloud'
    };
  };

  const formatLastSync = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const status = getSyncStatus();
  const IconComponent = status.icon;

  if (!showDetails) {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        <IconComponent
          className={`h-3 w-3 ${status.color} ${
            isSyncing ? 'animate-spin' : ''
          }`}
        />
        <span className={`text-xs ${status.color} hidden sm:inline`}>
          {status.label}
        </span>
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <IconComponent
            className={`h-4 w-4 ${status.color} ${
              isSyncing ? 'animate-spin' : ''
            }`}
          />
          <span className={`font-medium ${status.color}`}>
            {status.label}
          </span>
        </div>

        {(driveState.driveConnected || (isAuthenticated && user?.preferences?.enableCloudSync)) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleManualSync}
            disabled={isSyncing || !isOnline}
            className="h-7 px-2"
          >
            <RefreshCw className={`h-3 w-3 ${isSyncing ? 'animate-spin' : ''}`} />
          </Button>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        {status.description}
      </p>

      {/* Connection status indicator */}
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        {isOnline ? (
          <Wifi className="h-3 w-3 text-green-500" />
        ) : (
          <WifiOff className="h-3 w-3 text-red-500" />
        )}
        <span>{isOnline ? 'Online' : 'Offline'}</span>
      </div>

      {/* Sync frequency info */}
      {driveState.driveConnected && (
        <div className="text-xs text-muted-foreground">
          Google Drive connected &middot; Auto-sync on changes
        </div>
      )}
      {!driveState.driveConnected && isAuthenticated && user?.preferences?.enableCloudSync && (
        <div className="text-xs text-muted-foreground">
          Sync frequency: {user.preferences.syncInterval === 'realtime' ? 'Real-time' :
                          user.preferences.syncInterval === 'hourly' ? 'Every hour' :
                          user.preferences.syncInterval === 'daily' ? 'Daily' : 'Manual'}
        </div>
      )}
    </div>
  );
}